import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { usePortalAuth } from '../../contexts/PortalAuthContext';
import portalAPI from '../../services/portalAPI';
import PortalFooter from '../../components/Layout/PortalFooter';
import PasswordInput from '../../components/common/PasswordInput';
import '../../styles/portal-brand.css';

const PortalChangePassword: React.FC = () => {
  usePortalAuth();
  const navigate = useNavigate();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!currentPassword) {
      setError('Current password is required');
      return;
    }

    if (!newPassword) {
      setError('New password is required');
      return;
    }

    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (currentPassword === newPassword) {
      setError('New password must be different from current password');
      return;
    }

    setLoading(true);

    try {
      await portalAPI.post('/portal/password/change_password/', {
        current_password: currentPassword,
        new_password: newPassword,
        confirm_password: confirmPassword,
      });

      setSuccess(true);
      // Clear form
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');

      // Redirect after success
      setTimeout(() => {
        navigate('/portal/settings', {
          state: { message: 'Password changed successfully' }
        });
      }, 2000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="portal-page">
        <div className="portal-container">
          <div className="change-password-card">
            <div className="success-icon-large">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                <polyline points="22 4 12 14.01 9 11.01"/>
              </svg>
            </div>
            <h1>Password Changed!</h1>
            <p>Your password has been updated successfully.</p>
            <p className="redirect-text">Redirecting to settings...</p>
          </div>
        </div>
        <PortalFooter />
        <style>{styles}</style>
      </div>
    );
  }

  return (
    <div className="portal-page">
      <div className="portal-container">
        <div className="portal-header">
          <Link to="/portal/settings" className="back-link">
            &larr; Back to Settings
          </Link>
          <h1>Change Password</h1>
          <p className="portal-subtitle">Update your account password</p>
        </div>

        <div className="change-password-card">
          <form onSubmit={handleSubmit} className="change-password-form">
            <div className="form-group">
              <label htmlFor="currentPassword">Current Password</label>
              <PasswordInput
                id="currentPassword"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Enter your current password"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="newPassword">New Password</label>
              <PasswordInput
                id="newPassword"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
                minLength={8}
                required
              />
              <span className="field-hint">Must be at least 8 characters</span>
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm New Password</label>
              <PasswordInput
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                required
              />
            </div>

            {error && <div className="error-message">{error}</div>}

            <div className="form-actions">
              <Link to="/portal/settings" className="btn-secondary">
                Cancel
              </Link>
              <button
                type="submit"
                className="btn-primary"
                disabled={loading}
              >
                {loading ? 'Changing Password...' : 'Change Password'}
              </button>
            </div>
          </form>
        </div>
      </div>
      <PortalFooter />
      <style>{styles}</style>
    </div>
  );
};

const styles = `
  .change-password-card {
    max-width: 480px;
    margin: 0 auto;
    background: white;
    border-radius: 12px;
    padding: 2rem;
    box-shadow: 0 2px 12px rgba(0,0,0,0.08);
  }

  .change-password-form .form-group {
    margin-bottom: 1.25rem;
  }

  .change-password-form label {
    display: block;
    font-weight: 500;
    color: #333;
    margin-bottom: 0.5rem;
  }

  .change-password-form input {
    width: 100%;
    padding: 0.75rem 1rem;
    border: 1px solid #ddd;
    border-radius: 8px;
    font-size: 1rem;
    transition: border-color 0.2s, box-shadow 0.2s;
  }

  .change-password-form input:focus {
    outline: none;
    border-color: #2D5A27;
    box-shadow: 0 0 0 3px rgba(45, 90, 39, 0.1);
  }

  .field-hint {
    display: block;
    font-size: 0.8rem;
    color: #888;
    margin-top: 0.25rem;
  }

  .error-message {
    background: #FEE2E2;
    color: #DC2626;
    padding: 0.75rem 1rem;
    border-radius: 8px;
    font-size: 0.875rem;
    margin-bottom: 1rem;
  }

  .form-actions {
    display: flex;
    gap: 1rem;
    justify-content: flex-end;
    margin-top: 1.5rem;
    padding-top: 1.5rem;
    border-top: 1px solid #eee;
  }

  .btn-primary {
    background: #2D5A27;
    color: white;
    padding: 0.75rem 1.5rem;
    border: none;
    border-radius: 8px;
    font-size: 1rem;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.2s;
  }

  .btn-primary:hover:not(:disabled) {
    background: #1e4a1b;
  }

  .btn-primary:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .btn-secondary {
    background: #f5f5f5;
    color: #666;
    padding: 0.75rem 1.5rem;
    border: 1px solid #ddd;
    border-radius: 8px;
    font-size: 1rem;
    font-weight: 500;
    cursor: pointer;
    text-decoration: none;
    transition: background 0.2s;
  }

  .btn-secondary:hover {
    background: #eee;
  }

  .back-link {
    color: #2D5A27;
    text-decoration: none;
    font-size: 0.875rem;
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    margin-bottom: 0.5rem;
  }

  .back-link:hover {
    text-decoration: underline;
  }

  .success-icon-large {
    display: flex;
    justify-content: center;
    margin-bottom: 1.5rem;
  }

  .change-password-card h1 {
    text-align: center;
    color: #22c55e;
    margin: 0 0 0.5rem 0;
  }

  .change-password-card p {
    text-align: center;
    color: #666;
    margin: 0;
  }

  .redirect-text {
    color: #888 !important;
    font-size: 0.875rem;
    margin-top: 1rem !important;
  }

  @media (max-width: 600px) {
    .change-password-card {
      padding: 1.25rem;
      margin: 0 -1rem;
      border-radius: 0;
    }

    .form-actions {
      flex-direction: column-reverse;
    }

    .form-actions .btn-primary,
    .form-actions .btn-secondary {
      width: 100%;
      text-align: center;
    }
  }
`;

export default PortalChangePassword;
